/**
 * usePersistentState — a `useState` that mirrors its value into localStorage.
 *
 * The settings backend isn't built yet, so preference sections persist locally;
 * swapping in real APIs later only means replacing this hook with a fetch call.
 */
import { useCallback, useState } from 'react';

const PREFIX = 'invitecloud.settings.';

export function usePersistentState<T>(key: string, initial: T) {
  const storageKey = PREFIX + key;

  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw !== null ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });

  const update = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved =
          typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        try {
          localStorage.setItem(storageKey, JSON.stringify(resolved));
        } catch {
          /* storage unavailable — keep in-memory only */
        }
        return resolved;
      });
    },
    [storageKey],
  );

  return [value, update] as const;
}
